import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { msalInstance } from './msal';
import { all_routes } from '../router/all_routes';
import { persistedLog } from '../core/utils/devLogger';
import { getAllowedModules, MODULES } from '../core/utils/roleBasedAccess';
import { httpInterceptor } from '../service/http-interceptor';

const AuthCallback = () => {
	const navigate = useNavigate();

	const clearAuthAndRedirect = (err) => {
		localStorage.removeItem('Token');
		localStorage.removeItem('LoginInfo');
		localStorage.removeItem('Company');
		localStorage.removeItem('IsExternalCustomer');
		localStorage.removeItem('CustomerType');

		if (err?.name === 'InteractionRequiredAuthError' || err?.message?.includes('AADSTS')) {
			navigate('/signin', { replace: true });
		}
	};

	const handleRouting = async (account, userRoles) => {
		let allowedModules = []; // Declare here, assign later

		if (account && account.username) {
			const email = account.username;
			const domain = email.includes('@') ? email.split('@')[1] : '';
			if (domain && domain.toLowerCase() !== 'chemplastinc.com') {
				// External user: resolve customer by email domain
				try {
					const apiRes = await httpInterceptor.get(`Customer/getbydomain?domain=${domain}`);
					if (apiRes && apiRes.response) {
						const customerId = apiRes.response.id || apiRes.response.Id;
						if (customerId) {
							localStorage.setItem('Company', customerId);
							localStorage.setItem('IsExternalCustomer', 'true');
							localStorage.setItem('CustomerType', apiRes.response.CustomerType || apiRes.response.customerType || '');
							allowedModules = [MODULES.CUSTOMER_DASHBOARD]; // Assign here if customer
							persistedLog('AuthCallback customer forced role', { customerId: customerId });
						}
					}
				} catch (e) {
					console.error('Failed to check customer domain', e);
				}
			} else {
				localStorage.removeItem('IsExternalCustomer');
				// Let standard app retain its 'Company' cache without interference
			}
		}

		// Calculate allowedModules if not already set by customer logic
		if (allowedModules.length === 0) {
			allowedModules = getAllowedModules(userRoles);
		}

		// If the user has CCAMS_Cus or CCAMS_Logi role AND is from an external domain
		// (not chemplastinc.com), call getbydomain to resolve their customerId.
		// ShipmentAdministrator and other default roles are intentionally ignored here.
		const hasCcamsRole =
			Array.isArray(userRoles) &&
			userRoles.some((r) => r === 'CCAMS_Cus' || r === 'CCAMS_Logi');

		if (hasCcamsRole && account && account.username) {
			const email = account.username;
			const domain = email.includes('@') ? email.split('@')[1] : '';
			const isExternalDomain = domain && domain.toLowerCase() !== 'chemplastinc.com';

			if (isExternalDomain && !localStorage.getItem('Company')) {
				try {
					persistedLog('AuthCallback: CCAMS external user — calling getbydomain', { domain });
					const apiRes = await httpInterceptor.get(`Customer/getbydomain?domain=${domain}`);
					if (apiRes && apiRes.response) {
						const customerId = apiRes.response.id || apiRes.response.Id;
						if (customerId) {
							localStorage.setItem('Company', customerId);
							localStorage.setItem('CustomerType', apiRes.response.CustomerType || apiRes.response.customerType || '');
							// Force Customer Dashboard as the only destination for external CCAMS users
							allowedModules = [MODULES.CUSTOMER_DASHBOARD];
							persistedLog('AuthCallback: CCAMS external customer resolved', { customerId });
						} else {
							console.warn('AuthCallback: getbydomain returned no customerId for domain', domain);
						}
					}
				} catch (e) {
					console.error('AuthCallback: getbydomain failed for CCAMS external user', e);
				}
			}
		}

		console.log('AuthCallback: Allowed Modules', allowedModules);


		setTimeout(() => {
			if (allowedModules.includes(MODULES.ADMIN_DASHBOARD)) {
				persistedLog('AuthCallback navigate -> dashboard', { path: all_routes.dashboard })
				navigate(all_routes.dashboard, { replace: true })
			} else if (allowedModules.includes(MODULES.WMS)) {
				persistedLog('AuthCallback navigate -> shipmenttile', { path: all_routes.shipmenttile })
				navigate(all_routes.shipmenttile, { replace: true })
			} else if (allowedModules.includes(MODULES.PPMS)) {
				persistedLog('AuthCallback navigate -> production', { path: all_routes.production })
				navigate(all_routes.production, { replace: true })
			} else if (allowedModules.includes(MODULES.QMS)) {
				persistedLog('AuthCallback navigate -> inspectionplantile', { path: all_routes.inspectionplantile })
				navigate(all_routes.inspectionplantile, { replace: true })
			} else if (allowedModules.includes(MODULES.MMS)) {
				persistedLog('AuthCallback navigate -> moldreceiving', { path: all_routes.moldreceiving })
				navigate(all_routes.moldreceiving, { replace: true })
			} else if (allowedModules.includes(MODULES.AMS)) {
				persistedLog('AuthCallback navigate -> cell', { path: all_routes.cell })
				navigate(all_routes.cell, { replace: true })
			} else if (allowedModules.includes(MODULES.CUSTOMER_DASHBOARD)) {
				persistedLog('AuthCallback navigate -> shipments', { path: all_routes.shipments })
				navigate(all_routes.shipments, { replace: true })
			} else {
				persistedLog('AuthCallback fallback navigate -> signin (no roles)', { path: '/signin' })
				clearAuthAndRedirect(new Error('No assigned roles/customer account found.'));
			}
		}, 100);
	};


	useEffect(() => {
		try {
			console.log('AuthCallback: start', {
				locationHref: window.location?.href,
				locationHash: window.location?.hash,
				locationSearch: window.location?.search,
			})
			persistedLog('AuthCallback start', {
				locationHref: window.location?.href,
				locationHash: window.location?.hash,
				locationSearch: window.location?.search,
			})
			// snapshot accounts at entry
			const snapAccounts = msalInstance.getAllAccounts()
			sessionStorage.setItem('MSAL_ACCOUNTS', JSON.stringify(snapAccounts || []))
			if (snapAccounts && snapAccounts[0]) {
				sessionStorage.setItem('MSAL_ACTIVE_ACCOUNT', JSON.stringify(snapAccounts[0]))
			}
		} catch (_err) {
			// no-op
		}

		// ensure MSAL redirect processing; index initializes already, but safe to call
		let hasError = false;
		msalInstance
			.handleRedirectPromise()
			.then((result) => {
				try {
					if (result?.account) {
						console.log('AuthCallback: handleRedirectPromise Result', JSON.stringify(result));
						console.log('AuthCallback: handleRedirectPromise success', {
							username: result.account.username,
							homeAccountId: result.account.homeAccountId,
							scopes: result.scopes,
							expiresOn: result.expiresOn,
						})

						// persist id token claims for later viewing
						let userRoles = [];
						try {
							if (result.idTokenClaims) {
								sessionStorage.setItem('MSAL_LAST_IDTOKEN_CLAIMS', JSON.stringify(result.idTokenClaims))
								persistedLog('AuthCallback saved idTokenClaims', {
									keys: Object.keys(result.idTokenClaims || {}),
								})

								// Extract roles for redirection logic
								const idc = result.idTokenClaims || {}
								userRoles = Array.isArray(idc.roles) ? idc.roles : []

								// Save display name/email when available
								if (idc.name) localStorage.setItem('UserName', idc.name)
								if (idc.preferred_username) localStorage.setItem('UserEmail', idc.preferred_username)
							}
						} catch (_e) { }

						// debug: log full access token if present on redirect (some flows return it)
						try {
							const effectiveToken = result.accessToken || result.idToken;
							if (effectiveToken) {
								console.log('AuthCallback token available', { type: result.accessToken ? 'access_token' : 'id_token' })
								// persist to localStorage for app-wide usage
								try {
									localStorage.setItem('Token', effectiveToken)

									// Create a dummy LoginInfo object to satisfy isAuthenticated() check
									const loginInfo = {
										user: {
											firstName: result.account.name || 'User',
											lastName: '',
											id: result.account.homeAccountId,
											role: userRoles[0] || '',
											company: ''
										},
										token: effectiveToken
									};
									localStorage.setItem('LoginInfo', JSON.stringify(loginInfo));

									localStorage.setItem(
										'MSAL_Token_Summary',
										JSON.stringify({
											token_type: 'Bearer',
											scopes: result.scopes,
											expires_on_iso: result.expiresOn ? result.expiresOn.toISOString() : undefined,
											tenantId: result.tenantId,
											authority: result.authority,
											account: {
												username: result.account.username,
												homeAccountId: result.account.homeAccountId,
												environment: result.account.environment,
												localAccountId: result.account.localAccountId,
											},
										})
									)
									if (result.idToken) {
										localStorage.setItem('MSAL_IdToken', result.idToken)
									}
								} catch (_err) { }
							}
						} catch (_e) { }

						persistedLog('AuthCallback handleRedirectPromise success', {
							username: result.account.username,
							homeAccountId: result.account.homeAccountId,
							scopes: result.scopes,
							expiresOn: result.expiresOn,
						})

						// Decide destination based on allowed modules
						try {
							handleRouting(result.account, userRoles);
						} catch (_e) {
							navigate(all_routes.dashboard, { replace: true })
						}
					} else {
						const initErrorStr = sessionStorage.getItem('MSAL_INIT_ERROR');
						if (initErrorStr) {
							sessionStorage.removeItem('MSAL_INIT_ERROR');
							throw new Error(initErrorStr); // propagate to the catch block
						}

						console.log('AuthCallback: handleRedirectPromise success - no result')
						persistedLog('AuthCallback handleRedirectPromise success - no result', {})

						const accounts = msalInstance.getAllAccounts();
						if (accounts && accounts.length > 0) {
							let userRoles = [];
							try {
								const raw = sessionStorage.getItem('MSAL_LAST_IDTOKEN_CLAIMS');
								if (raw) {
									const claims = JSON.parse(raw);
									userRoles = Array.isArray(claims?.roles) ? claims.roles : [];
								}
							} catch (_err) { }
							handleRouting(accounts[0], userRoles);
						} else {
							navigate('/signin', { replace: true });
						}
					}
				} catch (_err) {
					if (_err?.message?.includes('AADSTS')) {
						throw _err;
					}
					// no-op for generic errors within the block
				}
			})
			.catch((err) => {
				hasError = true;
				try {
					console.error('AuthCallback: handleRedirectPromise error', {
						name: err?.name,
						message: err?.message,
						errorCode: err?.errorCode,
						subError: err?.subError,
					})
					persistedLog('AuthCallback handleRedirectPromise error', {
						name: err?.name,
						message: err?.message,
						errorCode: err?.errorCode,
						subError: err?.subError,
					}, 'error')

					// Trap Entra ID blockage (AADSTS50105) and manually check against external customer database
					let extractEmail = null;
					if (err?.message && err.message.includes('AADSTS50105')) {
						const extMatch = err.message.match(/'([^']+)#EXT#/);
						if (extMatch && extMatch[1]) {
							const parts = extMatch[1].split('_');
							if (parts.length > 1) {
								const domainPart = parts[parts.length - 1];
								const userPart = parts.slice(0, parts.length - 1).join('_');
								extractEmail = `${userPart}@${domainPart}`;
							} else {
								extractEmail = extMatch[1];
							}
						} else {
							const normalMatch = err.message.match(/'([^']+@[^']+)'/);
							if (normalMatch && normalMatch[1]) {
								extractEmail = normalMatch[1];
							}
						}
					}

					if (extractEmail) {
						const domain = extractEmail.includes('@') ? extractEmail.split('@')[1] : '';
						if (domain && domain.toLowerCase() !== 'chemplastinc.com') {
							// Using anonymous getbydomain API to check if they are a valid customer
							httpInterceptor.get(`Customer/getbydomain?domain=${domain}`)
								.then((apiRes) => {
									if (apiRes && apiRes.response) {
										const customerId = apiRes.response.id || apiRes.response.Id;
										if (customerId) {
											localStorage.setItem('Company', customerId);
											localStorage.setItem('IsExternalCustomer', 'true');
											localStorage.setItem('CustomerType', apiRes.response.CustomerType || apiRes.response.customerType || '');

											if (!localStorage.getItem('Token')) {
												localStorage.setItem('Token', 'external-customer-dummy-token');
												localStorage.setItem('LoginInfo', JSON.stringify({
													user: { firstName: 'External', role: '', company: customerId },
													token: 'external-customer-dummy-token'
												}));
											}

											persistedLog('AuthCallback customer forced role bypass', { customerId });
											setTimeout(() => {
												navigate(all_routes.shipments, { replace: true });
											}, 100);
										} else {
											throw new Error('Customer ID not found in API response');
										}
									} else {
										throw new Error('Invalid API response');
									}
								})
								.catch((customerErr) => {
									console.error('Customer fallback check failed:', customerErr);
									clearAuthAndRedirect(err);
								});
							return; // Yield control to the async http call
						}
					}

					// Fallback: Clear session if MSAL login completely breaks and no customer check passed
					clearAuthAndRedirect(err);

				} catch (_err) {
					// no-op
				}
			})
			.finally(() => {
				try {
					const accounts = msalInstance.getAllAccounts()
					sessionStorage.setItem('MSAL_ACCOUNTS', JSON.stringify(accounts || []))
					if (accounts && accounts[0]) {
						sessionStorage.setItem('MSAL_ACTIVE_ACCOUNT', JSON.stringify(accounts[0]))
					}

					// If we haven't navigated yet (e.g. no result but accounts exist) and there's no auth error, try to navigate
					if (!hasError && accounts && accounts.length > 0) {
						// We need to get roles again since we might be in the finally block without 'result'
						let userRoles = [];
						try {
							const raw = sessionStorage.getItem('MSAL_LAST_IDTOKEN_CLAIMS');
							if (raw) {
								const claims = JSON.parse(raw);
								userRoles = Array.isArray(claims?.roles) ? claims.roles : [];
							}
						} catch (_e) { }

						handleRouting(accounts[0], userRoles);
					}
				} catch (_err) {
					// no-op
				}
			});
	}, []);

	return null;
};

export default AuthCallback;
